import { ORGANIZATION_CODES } from '@revenuemarket/bank';
import { Flex } from '@revenuemarket/cds';
import { theme } from '@revenuemarket/design';
import { ResponseStatus } from '@revenuemarket/http';
import { overlays } from '@revenuemarket/overlays';
import { useFullSpinnerStore } from '@revenuemarket/react';
import { AccountStatus } from '@revenuemarket/trade';
import { Toast, Typography } from '@revenuemarket/ui';
import { unique } from '@revenuemarket/utils';
import { Button, Drawer } from 'antd';

import BankAccountScrapeErrorSummary from '~components/ui/layout/BankAccountScrapeErrorSummary';
import { useSyncCertificatesOrganizations } from '~hooks/queries/certificates/query';
import useGetIntegratedScrapeStatus from '~hooks/queries/scraping/useGetIntegratedScrapeStatus';
import { useCompanyId } from '~hooks/useCompanyId';

import CertificateRegisterModal from '../Modal/CertificateRegisterModal';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  accountType?: 'bank' | 'security';
}

const ScrapeErrorsDrawer = ({ isOpen, onClose, accountType = 'bank' }: Props) => {
  const { showFullSpinner, hideFullSpinner } = useFullSpinnerStore();
  const { companyId } = useCompanyId();

  const { data: scrapeStatus } = useGetIntegratedScrapeStatus(companyId);

  const abnormalBankAccounts = scrapeStatus?.Data?.data.abnormalBankAccounts ?? [];
  const abnormalSecurityAccounts = scrapeStatus?.Data?.data.abnormalSecurityAccounts ?? [];

  const abnormalAccounts = accountType === 'bank' ? abnormalBankAccounts : abnormalSecurityAccounts;

  const deletedAbnormalAccounts = abnormalAccounts.filter(
    (account) => account.status === AccountStatus.CertificateDeleted,
  );
  const certificateNotRegisteredAccounts = abnormalAccounts.filter(
    (account) => account.status === AccountStatus.CertificateNotRegistered,
  );

  const { mutateAsync: syncCertificatesOrganizations } = useSyncCertificatesOrganizations();

  if (abnormalAccounts.length === 0) {
    onClose();
    return null;
  }

  return (
    <Drawer title="계좌 오류 현황" placement={'right'} width={500} onClose={onClose} open={isOpen}>
      <Flex direction="column" gap={24}>
        <BankAccountScrapeErrorSummary
          title="공동인증서 만료 계좌"
          bankAccounts={abnormalAccounts.filter(
            (account) => account.status === AccountStatus.CertificateExpired,
          )}
          description={
            <>
              아래 계좌에 연결된 공동인증서가 만료되었습니다. 공동인증서 갱신 또는 재발급 후{' '}
              <Typography variant="body1" color={theme.colorTokens.textDefault}>
                공동인증서 등록
              </Typography>
              을 진행해주세요. 더이상 존재하지 않거나 사용하지 않는 계좌라면{' '}
              <Typography variant="body1" color={theme.colorTokens.textDefault}>
                수집 중단
              </Typography>
              를 클릭해주세요.
            </>
          }
          actionSlot={
            <Button
              onClick={() => {
                overlays.open(({ isOpen, onClose }) => {
                  return <CertificateRegisterModal open={isOpen} onCancel={onClose} />;
                });
              }}
            >
              공동인증서 등록
            </Button>
          }
        />
        <BankAccountScrapeErrorSummary
          title="공동인증서 폐기 계좌"
          bankAccounts={abnormalAccounts.filter(
            (account) => account.status === AccountStatus.CertificateDestroyed,
          )}
          description={
            <>
              아래 계좌에 연결된 공동인증서가 폐기되었습니다. 공동인증서 갱신 또는 재발급 후
              공동인증서를 새로 등록해주세요. 더이상 존재하지 않거나 사용하지 않는 계좌라면{' '}
              <Typography variant="body1" color={theme.colorTokens.textDefault}>
                수집 중단
              </Typography>
              를 클릭해주세요.
            </>
          }
          actionSlot={
            <Button
              onClick={() => {
                overlays.open(({ isOpen, onClose }) => {
                  return <CertificateRegisterModal open={isOpen} onCancel={onClose} />;
                });
              }}
            >
              공동인증서 등록
            </Button>
          }
        />
        <BankAccountScrapeErrorSummary
          title="공동인증서 삭제 계좌"
          bankAccounts={deletedAbnormalAccounts}
          description={
            <>
              아래 계좌에 연결된 공동인증서를 삭제하셨습니다.{' '}
              <Typography variant="body1" color={theme.colorTokens.textDefault}>
                {unique(
                  deletedAbnormalAccounts.map(
                    (account) => ORGANIZATION_CODES[account.organizationCode],
                  ),
                ).join(', ')}
              </Typography>
              에 로그인 가능한 공동인증서 등록을 진행해주세요. 더이상 존재하지 않거나 사용하지 않는
              계좌라면{' '}
              <Typography variant="body1" color={theme.colorTokens.textDefault}>
                수집 중단
              </Typography>
              를 클릭해주세요.
            </>
          }
          actionSlot={
            <Button
              onClick={() => {
                overlays.open(({ isOpen, onClose }) => {
                  return <CertificateRegisterModal open={isOpen} onCancel={onClose} />;
                });
              }}
            >
              공동인증서 등록
            </Button>
          }
        />
        <BankAccountScrapeErrorSummary
          title="공동인증서 기관 미등록 계좌"
          bankAccounts={certificateNotRegisteredAccounts}
          description={
            <>
              공동인증서를 갱신 또는 재발급하셨나요? 새로 발급한 공동인증서를{' '}
              <Typography variant="body1" color={theme.colorTokens.textDefault}>
                {unique(
                  certificateNotRegisteredAccounts.map(
                    (account) => ORGANIZATION_CODES[account.organizationCode],
                  ),
                ).join(', ')}
              </Typography>
              에 등록한 뒤 연결 기관 갱신을 클릭해주세요. 더이상 존재하지 않거나 사용하지 않는
              계좌라면{' '}
              <Typography variant="body1" color={theme.colorTokens.textDefault}>
                수집 중단
              </Typography>
              를 클릭해주세요.
            </>
          }
          actionSlot={
            <Button
              onClick={async () => {
                showFullSpinner('공동인증서에 연결된 기관을 다시 불러오는 중입니다');
                const response = await syncCertificatesOrganizations({ companyId });
                if (response.Status === ResponseStatus.SUCCESS) {
                  Toast.success('공동인증서에 연결된 기관이 갱신되었습니다.');
                } else {
                  Toast.error('공동인증서에 연결된 기관을 불러오는 데에 실패하였습니다.');
                }
                hideFullSpinner();
              }}
            >
              연결 기관 갱신
            </Button>
          }
        />
        <BankAccountScrapeErrorSummary
          title="찾을 수 없는 계좌"
          bankAccounts={abnormalAccounts.filter(
            (account) => account.status === AccountStatus.AccountNotExist,
          )}
          description={
            <>
              은행에서 아래 계좌를 찾을 수 없습니다. 더이상 존재하지 않거나 사용하지 않는 계좌라면{' '}
              <Typography variant="body1" color={theme.colorTokens.textDefault}>
                수집 중단
              </Typography>
              를 클릭해주세요.
            </>
          }
          actionSlot={<Button id="intercom-launcher">클로브 팀 문의</Button>}
        />
        <BankAccountScrapeErrorSummary
          title="조회 권한 없음 계좌"
          bankAccounts={abnormalAccounts.filter(
            (account) => account.status === AccountStatus.NoPermission,
          )}
          description={
            <>
              아래 계좌에 대한 조회 권한이 없습니다. 인터넷뱅킹에서 해당 계좌의 조회 권한을
              확인해주세요. 더이상 존재하지 않거나 사용하지 않는 계좌라면{' '}
              <Typography variant="body1" color={theme.colorTokens.textDefault}>
                수집 중단
              </Typography>
              를 클릭해주세요.
            </>
          }
          actionSlot={<Button id="intercom-launcher">클로브 팀 문의</Button>}
        />
        <BankAccountScrapeErrorSummary
          title="중지 계좌"
          bankAccounts={abnormalAccounts.filter(
            (account) => account.status === AccountStatus.AccountSuspended,
          )}
          description={
            <>
              아래 계좌가 중지 상태입니다. 해당 은행에 문의하여 계좌 상태를 확인해주세요. 더이상
              존재하지 않거나 사용하지 않는 계좌라면{' '}
              <Typography variant="body1" color={theme.colorTokens.textDefault}>
                수집 중단
              </Typography>
              를 클릭해주세요.
            </>
          }
          actionSlot={<Button id="intercom-launcher">클로브 팀 문의</Button>}
        />
        <BankAccountScrapeErrorSummary
          title="수집 불가 계좌"
          bankAccounts={abnormalAccounts.filter(
            (account) =>
              account.status === AccountStatus.Error ||
              account.status === AccountStatus.Unknown ||
              account.status === AccountStatus.CertificateUnknown ||
              account.status === AccountStatus.AccountUnscrapable,
          )}
          description={
            <>
              은행에서 거래내역을 제공하지 않는 계좌입니다. 더이상 존재하지 않거나 사용하지 않는
              계좌라면{' '}
              <Typography variant="body1" color={theme.colorTokens.textDefault}>
                수집 중단
              </Typography>
              를 클릭해주세요.
            </>
          }
          actionSlot={<Button id="intercom-launcher">클로브 팀 문의</Button>}
        />
      </Flex>
    </Drawer>
  );
};

export default ScrapeErrorsDrawer;
